import React, { useState } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import ATSPlansModal from "@/components/ats/ATSPlansModal";
import { LeadFormModal } from "@/components/modals/LeadFormModal";
import sphereImg from "@/assets/nexus-sphere.png";

const metrics = [
  { value: "+500", label: "Empresas activas" },
  { value: "+17", label: "Job boards integrados" },
  { value: "1M+", label: "Candidatos en red" },
  { value: "50%", label: "Menos tiempo de contratación" },
];

const NexusHero: React.FC = () => {
  const [leadOpen, setLeadOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);

  return (
    <section
      id="inicio"
      className="relative min-h-[100svh] md:min-h-dvh flex items-center overflow-hidden pt-24 md:pt-28"
      style={{
        background: "linear-gradient(135deg, #0B0E2D 0%, #1A1060 100%)",
      }}
    >
      {/* Decorative cyan glow */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #00C9D4 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #620AE2 0%, transparent 70%)" }}
      />

      {/* Nexus signature sphere */}
      <img
        src={sphereImg}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute -left-40 -bottom-32 w-[640px] h-[640px] object-contain opacity-25 mix-blend-screen pointer-events-none select-none"
        loading="eager"
        decoding="async"
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <span
            className="inline-block px-4 py-1.5 rounded-full border text-xs md:text-sm font-medium mb-6"
            style={{ borderColor: "#00C9D4", color: "#00C9D4", background: "transparent" }}
          >
            Para empresas que contratan talento en Costa Rica
          </span>

          <h1
            className="font-exo font-bold text-white leading-[1.05] mb-6"
            style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}
          >
            El ecosistema de reclutamiento para empresas{" "}
            <span style={{ color: "#00C9D4" }}>#1</span> en Costa Rica
          </h1>

          <p
            className="mb-10 mx-auto max-w-2xl"
            style={{ color: "#BBBBDD", fontSize: "18px", lineHeight: 1.6 }}
          >
            Tecnología ATS + psicometría + bolsa de empleo: todo lo que su equipo de RRHH
            necesita para contratar mejor, más rápido y con datos reales.
          </p>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-10 max-w-3xl mx-auto">
            {metrics.map((m) => (
              <div key={m.label}>
                <div className="text-2xl md:text-3xl font-bold text-white font-exo">{m.value}</div>
                <div className="text-xs md:text-sm mt-1" style={{ color: "#BBBBDD" }}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => setLeadOpen(true)}
              className="rounded-md font-semibold h-12 px-6 bg-[#620AE2] text-white border-[1.5px] border-[#620AE2] hover:bg-white hover:text-[#620AE2] hover:border-[#620AE2] transition-all duration-200"
            >
              Solicitar asesoría personalizada
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              onClick={() => setPlansOpen(true)}
              className="rounded-md h-12 px-6 bg-transparent text-white border-[1.5px] border-white hover:bg-[#620AE2] hover:text-white hover:border-[#620AE2] transition-all duration-200"
            >
              Ver planes
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 cursor-pointer"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        onClick={() => {
          const nextSection = document.getElementById("lead-form");
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <span className="text-xs font-medium tracking-wider uppercase" style={{ color: "#BBBBDD" }}>
          Descubra más
        </span>
        <ChevronDown className="w-5 h-5" style={{ color: "#00C9D4" }} />
      </motion.div>

      <LeadFormModal
        isOpen={leadOpen}
        onClose={() => setLeadOpen(false)}
        route="/solicitar-asesoria"
      />
      <ATSPlansModal isOpen={plansOpen} onClose={() => setPlansOpen(false)} />
    </section>
  );
};

export default NexusHero;
